/**
 * MSAL configuration — Entra ID workforce tenant, admins only (architecture-proposal
 * Decision 5). Members never log in here; they arrive via the Telegram deep link (§3.3).
 *
 * SPA app registration = public client, Auth Code + PKCE. No client secret exists.
 */
import { LogLevel, PublicClientApplication, type Configuration } from '@azure/msal-browser'

import { env } from '~/lib/env.ts'

export const msalConfig: Configuration = {
  auth: {
    clientId: env.VITE_ENTRA_SPA_CLIENT_ID,
    // Single-tenant authority. NOT /common: /common would let any Microsoft account
    // reach the sign-in page, which is the same defect that rules out SWA built-in auth.
    authority: `https://login.microsoftonline.com/${env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage over localStorage: the token dies with the tab. This is a finance
    // back-office for ~15 people, not a consumer app; a shorter window is cheap here.
    // (msal-browser v5 dropped `navigateToLoginRequestUrl` and `storeAuthStateInCookie`;
    // both were IE/legacy-redirect accommodations and neither has a v5 equivalent.)
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (level === LogLevel.Error) console.error(message)
      },
    },
  },
}

/**
 * The scope we request for the API.
 *
 * ⚠️ This must be the *API* app registration's scope, not the SPA's. The API validates
 * `aud` against its own client id (§3.2, "Identity") — a token minted for Graph is validly
 * signed and must be rejected. Requesting the wrong scope here is the most common Entra
 * bug and it fails at the API, confusingly, not here.
 */
export const apiScopes = [env.VITE_ENTRA_API_SCOPE]

export const loginRequest = { scopes: apiScopes }

export const msalInstance = new PublicClientApplication(msalConfig)

/**
 * Acquires an access token silently, falling back to a redirect when the refresh token has
 * expired or consent is missing. Returns null when nobody is signed in.
 */
export async function acquireAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) return null

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account })
    return result.accessToken
  } catch {
    // Silent acquisition failed (expired session / new consent required). A redirect is the
    // only honest recovery; the promise never resolves because the page unloads.
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account })
    return null
  }
}
